import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "@/components/Login";
import Dashboard from "@/components/Dashboard";
import LearnMore from "@/components/LearnMore";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import VersionCheck from "@/components/VersionCheck";
import { usePageView } from "@/hooks/usePageView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
// In production, always use the current domain to avoid cross-origin issues
const IS_PRODUCTION = !window.location.hostname.includes('preview.emergentagent.com') && !window.location.hostname.includes('localhost');
export const BASE_URL = IS_PRODUCTION ? window.location.origin : BACKEND_URL;
export const API = `${BASE_URL}/api`;

// Clean up old service workers on app load
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
      console.log('Old service worker unregistered on app load');
    });
  });
}

// Clear old caches on app load
if ('caches' in window) {
  caches.keys().then(cacheNames => {
    cacheNames.forEach(cacheName => {
      caches.delete(cacheName);
      console.log('Old cache cleared on app load:', cacheName);
    });
  });
}

// Axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Axios response interceptor for handling auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // If we get a 401 Unauthorized, log out the user
    if (error.response && error.response.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function PageViewTracker() {
  usePageView();
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
      }
    }
    setIsLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="text-xl font-medium text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App min-h-screen bg-slate-950">
        <BrowserRouter>
          <PageViewTracker />
          <Routes>
            <Route
              path="/login"
              element={
                !isAuthenticated ? (
                  <Login onLogin={handleLogin} />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/dashboard/*"
              element={
                isAuthenticated ? (
                  <Dashboard user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            <Route
              path="/"
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              }
            />
            <Route
              path="/learn-more"
              element={<LearnMore />}
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" expand={false} richColors />
        <VersionCheck />
      </div>
    </ErrorBoundary>
  );
}

export default App;
